/**
 * As medidas e o comportamento das caixas do banco de horas que reproduzem os
 * modais da origem.
 *
 * Papel: aqui ficam `.form-grid`, `.form-group`, o rótulo e o campo da origem
 * em classes do Tailwind, e os dois manipuladores que fazem o `Dialog` do Radix
 * se comportar como o `.modal-overlay`: não fechar por `Esc` nem por clique ou
 * foco fora, e abrir com o foco na própria caixa.
 *
 * Como usar: toda caixa do banco de horas passa `handleImpedirFechamento` a
 * `onEscapeKeyDown`, `onPointerDownOutside` e `onInteractOutside` do
 * `DialogContent`, e `handleAbrir` a `onOpenAutoFocus`. A origem não tem
 * ouvinte de `Esc` nem de clique no véu: a caixa fecha só por "Cancelar" e pela
 * gravação, e um `Esc` sem querer apagaria o que a pessoa digitou.
 */

/** A grade de duas colunas iguais, com 16px entre os campos — `.form-grid`. */
export const CLASSES_DA_GRADE = 'grid [grid-template-columns:1fr_1fr] gap-4';

/** O grupo de rótulo e campo, com 6px entre os dois — `.form-group`. */
export const CLASSES_DO_GRUPO = 'flex flex-col gap-1.5';

/** O grupo que ocupa a linha inteira — `.form-group.full`. */
export const CLASSES_DO_GRUPO_INTEIRO = 'col-span-full flex flex-col gap-1.5';

/**
 * A entrelinha do rótulo da origem, que herda o 1,6 do `body`; o `Label` é
 * `leading-none`, e sem isto cada rótulo ficaria uns 6px mais baixo.
 */
export const CLASSES_DO_ROTULO = 'leading-[1.6]';

/**
 * O que o campo de texto muda no `Input` para chegar à origem: a entrelinha
 * `normal` do campo nativo e o cinza e a opacidade de exemplo do navegador.
 */
export const CLASSES_DO_CAMPO =
  'leading-[normal] placeholder:[color:revert]! placeholder:[opacity:revert]!';

/** O `<select>` nativo com as medidas do `Input`. */
export const CLASSES_DO_SELECT =
  'rounded-controle border border-linha-forte bg-background px-3 py-2.5 text-sm text-foreground outline-none focus-visible:border-brass';

/**
 * Impede o Radix de fechar a caixa por `Esc` ou por toque e foco fora dela.
 *
 * @param evento - O evento que o Radix entregaria ao fechamento.
 */
export function handleImpedirFechamento(evento: Event) {
  evento.preventDefault();
}

/**
 * Põe o foco na própria caixa ao abrir, em vez do primeiro campo, para ela não
 * nascer com a borda de foco que a origem não mostra.
 *
 * @param evento - O evento de foco inicial do Radix, disparado na caixa.
 */
export function handleAbrir(evento: Event) {
  evento.preventDefault();
  if (evento.currentTarget instanceof HTMLElement) evento.currentTarget.focus();
}
